import type { ReactNode } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  isCannedFieldElement,
  isCannedFieldEntry,
  isCrossReferenceElement,
  isCrossReferenceEntry,
  isLinkElement,
  isLinkEntry,
  isNonSignatureFieldElement,
  isNonSignatureFieldEntry,
  Queries,
} from '@aline/common';
import { Paper } from '@mui/material';
import { equals } from 'ramda';
import { Editor, Range } from 'slate';
import { AlineButton, defaultEditorPopoverPaperStyles } from 'src/components';
import { SelectionRibbon } from 'src/editor';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import {
  CannedFieldPopoverContent,
  closeSplitTableCellsPopover,
  CrossReferencePopoverContent,
  InsertLinkForm,
  LinkPopoverContent,
  SplitTableCellsPopoverContent,
  useInsertLink,
  useSplitTableCellsPopoverOpen,
  VariablePopoverContent,
} from 'src/editor/popovers';
import { ClientTransforms } from 'src/editor/transforms';
import { IS_MOBILE } from 'src/helpers/environment';
import { makeStyles } from 'tss-react/mui';

import { useOpenHeaderOrFooterPageNum } from './HeaderOrFooter';
import { RangePopover } from './RangePopover';
import { useEditor, useRangeRects, useSelection, useSlateSelector } from '../hooks';

export function SelectionPopover() {
  const selection = useSelection();
  const activeHeaderOrFooterPageNum = useOpenHeaderOrFooterPageNum();
  const isDraggingSelection = useIsDraggingSelection();
  const insertLinkState = useInsertLink();

  /**
   * A selection that covers whole table cells anchors to their boxes rather than to a linear range:
   * a grid selection's range walks the anchor→focus text run, and a single cell's traces only the
   * text in it, so either would put the popover off the middle of what is selected.
   */
  const cellSelectionRects = useSlateSelector(
    editor => Queries.getSelectedTableCellRectsInDocument(editor, { activeHeaderOrFooterPageNum }),
    equals
  );
  const isCellSelection = Boolean(cellSelectionRects);
  const isSplitTableCellsOpen = useSplitTableCellsPopoverOpen();
  const isSelectionWithinTable = useSlateSelector(
    editor => Queries.getIsSelectionWithinTable(editor).isSelectionWithinTable
  );

  useEffect(() => {
    if (!isSelectionWithinTable) closeSplitTableCellsPopover();
  }, [isSelectionWithinTable]);

  const selectedElementState = useSlateSelector(editor => {
    const nodeEntry = Editor.above(editor, {
      match: node =>
        isLinkElement(node) ||
        isCrossReferenceElement(node) ||
        isNonSignatureFieldElement(node) ||
        isCannedFieldElement(node),
    });
    if (!nodeEntry) return null;
    const cMRange = Editor.range(editor, nodeEntry[1]);
    return { cMRange, nodeEntry };
  }, equals);

  const { cMRange, content, isAnchoredToCells, placement } = useMemo((): {
    cMRange: Range | null;
    content: ReactNode | null;
    /** Whether the popover is about the selection itself, so it anchors to the selected cells. */
    isAnchoredToCells: boolean;
    placement: 'top' | 'bottom' | null;
  } => {
    if (insertLinkState)
      return {
        cMRange: insertLinkState.insertLinkRange,
        content: <InsertLinkForm {...insertLinkState} />,
        isAnchoredToCells: false,
        placement: 'bottom',
      };

    if (isSplitTableCellsOpen && selection)
      return {
        cMRange: isCellSelection ? null : selection,
        content: <SplitTableCellsPopoverContent />,
        isAnchoredToCells: isCellSelection,
        placement: 'bottom',
      };

    if (selectedElementState && !IS_MOBILE) {
      const { cMRange, nodeEntry } = selectedElementState;

      if (isLinkEntry(nodeEntry))
        return {
          cMRange,
          content: <LinkPopoverContent linkEntry={nodeEntry} />,
          isAnchoredToCells: false,
          placement: 'bottom',
        };

      if (isCrossReferenceEntry(nodeEntry))
        return {
          cMRange,
          content: <CrossReferencePopoverContent crossReferenceEntry={nodeEntry} />,
          isAnchoredToCells: false,
          placement: 'bottom',
        };

      if (isNonSignatureFieldEntry(nodeEntry))
        return {
          cMRange,
          content: <VariablePopoverContent fieldEntry={nodeEntry} />,
          isAnchoredToCells: false,
          placement: 'bottom',
        };

      if (isCannedFieldEntry(nodeEntry))
        return {
          cMRange,
          content: <CannedFieldPopoverContent cannedFieldEntry={nodeEntry} />,
          isAnchoredToCells: false,
          placement: 'bottom',
        };
    }

    if (selection && !isDraggingSelection && Range.isExpanded(selection))
      return {
        cMRange: isCellSelection ? null : selection,
        /**
         * The document is canvas-rendered, so iOS native long-press selection
         * and the system Copy callout can't operate on it. On mobile we
         * replace the desktop ribbon with a single Copy affordance that fires
         * the same copyToClipboard path as Cmd+C.
         */
        content: IS_MOBILE ? <MobileCopyButton /> : <SelectionRibbon />,
        isAnchoredToCells: isCellSelection,
        placement: 'top',
      };

    return { cMRange: null, content: null, isAnchoredToCells: false, placement: null };
  }, [
    insertLinkState,
    isSplitTableCellsOpen,
    selectedElementState,
    selection,
    isDraggingSelection,
    isCellSelection,
  ]);

  const linearRangeRects = useRangeRects(cMRange);
  const rangeRects = isAnchoredToCells ? cellSelectionRects : linearRangeRects;

  if (!placement) return null;

  return (
    <RangePopover gap={IS_MOBILE ? 36 : undefined} placement={placement} rangeRects={rangeRects}>
      {content}
    </RangePopover>
  );
}

const useMobileCopyButtonStyles = makeStyles()(theme => ({
  button: {
    fontWeight: 600,
    minWidth: 0,
    padding: theme.spacing(1, 2.5),
  },
  paper: {
    ...defaultEditorPopoverPaperStyles,
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    maxWidth: 'unset',
    minWidth: 'unset',
    /** Prevent the page from scrolling when the user is trying to tap Copy. */
    touchAction: 'none',
  },
}));

function MobileCopyButton(): JSX.Element {
  const editor = useEditor();
  const { classes } = useMobileCopyButtonStyles();
  const [copied, setCopied] = useState(false);

  async function handleCopy(): Promise<void> {
    const success = await ClientTransforms.copyToClipboard(editor);
    if (!success) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Paper className={classes.paper}>
      <AlineButton className={classes.button} color="primary" onClick={handleCopy}>
        {copied ? 'Copied' : 'Copy'}
      </AlineButton>
    </Paper>
  );
}

function useIsDraggingSelection() {
  /**
   * We clear mouseDownClick onMouseUp so a user is "dragging" whenever there is a mouseDownClick
   * registered.
   */
  return Boolean(useEditorTabStateSelector(state => state.click.mouseDownClick));
}
